import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Loader from '../../../../components/ui/Loader/Loader';
import { apiRunWithAxios } from './APIRunWithAxios';
import { Modal } from 'bootstrap';
import uuid from 'react-uuid';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

const startIcon = new L.Icon({
    iconUrl: 'https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-2x-green.png',
    iconSize: [25, 25],
    iconAnchor: [15, 25],
    popupAnchor: [0, -25]
});

const endIcon = new L.Icon({
    iconUrl: 'https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-2x-red.png',
    iconSize: [25, 25],
    iconAnchor: [15, 25],
    popupAnchor: [0, -25]
});

const carIcon = new L.Icon({
    iconUrl: 'https://maps.google.com/mapfiles/kml/shapes/cabs.png',
    iconSize: [30, 20],
    iconAnchor: [20, 10],
    popupAnchor: [0, -10]
});

const LeafletMap = ({ points, startPoint, endPoint, onReplay, replayTrigger }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const animationRef = useRef(null);
    const carMarkerRef = useRef(null); // This will hold the actual marker instance
    const polylineRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [showReplayBtn, setShowReplayBtn] = useState(false);

    // Function to start animation
    const startAnimation = () => {
        if (!points || points.length < 2) {
            return;
        }

        setIsPlaying(true);
        setShowReplayBtn(false);

        // Reset car to start
        if (carMarkerRef.current) {
            carMarkerRef.current.setLatLng([points[0].lat, points[0].lng]);
        }

        const totalPathLength = points.reduce((total, point, index) => {
            if (index === 0) return total;
            const prev = points[index - 1];
            const latDiff = point.lat - prev.lat;
            const lngDiff = point.lng - prev.lng;
            return total + Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
        }, 0);

        const SPEED = totalPathLength / 5000;

        let currentSegment = 0;
        let progress = 0;
        let lastTime = null;

        // Cancel any existing animation
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }

        const animate = (timestamp) => {
            if (!lastTime) {
                lastTime = timestamp;
            }

            const deltaTime = timestamp - lastTime;
            lastTime = timestamp;

            if (currentSegment >= points.length - 1) {
                if (carMarkerRef.current) {
                    carMarkerRef.current.setLatLng([points[points.length - 1].lat, points[points.length - 1].lng]);
                }
                animationRef.current = null;
                setIsPlaying(false);
                setShowReplayBtn(true);
                if (onReplay) {
                    onReplay(false);
                }
                return;
            }

            const start = points[currentSegment];
            const end = points[currentSegment + 1];

            const latDiff = end.lat - start.lat;
            const lngDiff = end.lng - start.lng;
            const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);

            if (distance === 0) {
                currentSegment++;
                animationRef.current = requestAnimationFrame(animate);
                return;
            }

            progress += (deltaTime * SPEED) / distance;

            if (progress >= 1) {
                currentSegment++;
                progress = 0;
                if (carMarkerRef.current) {
                    carMarkerRef.current.setLatLng([end.lat, end.lng]);
                }

                if (currentSegment >= points.length - 1) {
                    animationRef.current = null;
                    setIsPlaying(false);
                    setShowReplayBtn(true);
                    if (onReplay) {
                        onReplay(false);
                    }
                    return;
                }
            } else {
                const lat = start.lat + (end.lat - start.lat) * progress;
                const lng = start.lng + (end.lng - start.lng) * progress;
                if (carMarkerRef.current) {
                    carMarkerRef.current.setLatLng([lat, lng]);
                }
            }

            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);
    };

    // Trigger replay when replayTrigger changes
    useEffect(() => {
        if (replayTrigger > 0 && points && points.length > 0) {
            startAnimation();
        }
    }, [replayTrigger]);

    useEffect(() => {
        if (!mapContainerRef.current || !points || points.length === 0) {
            return;
        }

        const map = L.map(mapContainerRef.current, {
            zoomControl: true,
            zoomAnimation: true,
            fadeAnimation: true,
            markerZoomAnimation: true
        });
        mapRef.current = map;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">Tappetbox</a>'
        }).addTo(map);

        const latLngs = points.map(point => [point.lat, point.lng]);
        const polyline = L.polyline(latLngs, {
            color: '#1677ff',
            weight: 6,
            opacity: 1
        }).addTo(map);
        polylineRef.current = polyline;

        if (startPoint) {
            L.marker([startPoint.lat, startPoint.lng], { icon: startIcon })
                .bindPopup(`
                    <div style="min-width:220px; padding:8px;">
                        <div style="font-size:16px; font-weight:700; margin-bottom:10px; color:#4caf50;">
                            🟢 Start Point
                        </div>
                        <div style="margin-bottom:5px;">
                            <b>Latitude:</b> ${startPoint.lat.toFixed(6)}
                        </div>
                        <div>
                            <b>Longitude:</b> ${startPoint.lng.toFixed(6)}
                        </div>
                    </div>
                `)
                .addTo(map);
        }

        if (endPoint) {
            L.marker([endPoint.lat, endPoint.lng], { icon: endIcon })
                .bindPopup(`
                    <div style="min-width:220px; padding:8px;">
                        <div style="font-size:16px; font-weight:700; margin-bottom:10px; color:#ef2929;">
                            🔴 End Point
                        </div>
                        <div style="margin-bottom:5px;">
                            <b>Latitude:</b> ${endPoint.lat.toFixed(6)}
                        </div>
                        <div>
                            <b>Longitude:</b> ${endPoint.lng.toFixed(6)}
                        </div>
                    </div>
                `)
                .addTo(map);
        }

        const bounds = L.latLngBounds(latLngs);
        map.fitBounds(bounds, { padding: [80, 80], maxZoom: 20 });

        // Initialize car at start position
        const carMarker = L.marker([points[0].lat, points[0].lng], { icon: carIcon }).addTo(map);
        carMarkerRef.current = carMarker;

        // Start animation automatically
        startAnimation();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [points, startPoint, endPoint]); 

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />
            <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                zIndex: 1000,
                background: '#fff',
                borderRadius: '10px',
                padding: '16px 18px',
                boxShadow: '0 2px 10px rgba(0,0,0,.18)',
                minWidth: '160px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
                    <span style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        background: '#4caf50',
                        border: '3px solid white',
                        boxShadow: '0 0 0 2px #4caf50',
                        marginRight: '12px'
                    }} />
                    <span>Start Point</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
                    <span style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        background: '#ef2929',
                        border: '3px solid white',
                        boxShadow: '0 0 0 2px #ef2929',
                        marginRight: '12px'
                    }} />
                    <span>End Point</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
                    <span style={{
                        width: '22px',
                        height: '4px',
                        background: '#1677ff',
                        marginRight: '12px'
                    }} />
                    <span>Route</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '21px', width: '22px', marginRight: '12px', textAlign: 'center' }}>
                        🚗
                    </span>
                    <span>Moving Vehicle</span>
                </div>
            </div>
        </div>
    );
};

const AnimatedMap = forwardRef(({
    imei,
    startDate,
    endDate,
    onClose,
    formData,
    data,
}, ref) => {

    const [loading, setLoading] = useState(false);
    const [points, setPoints] = useState([]);
    const [startPoint, setStartPoint] = useState(null);
    const [endPoint, setEndPoint] = useState(null);
    const [mapError, setMapError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [replayTrigger, setReplayTrigger] = useState(0);

    const modalElem = useRef(null);
    const modal = useRef(null);
    const [modalId] = useState(uuid());

    const access_token = useSelector(state => state.auth.access_token);

    useImperativeHandle(ref, () => ({
        openMap: (params = {}) => {
            setLoading(true);
            setMapError(null);
            setPoints([]);
            setStartPoint(null);
            setEndPoint(null);
            setIsPlaying(false);
            setReplayTrigger(prev => prev + 1);
            modalOpen();

            if (params.data) {
                processHistoricalData(params.data);
            }
            if (params.imei && params.startDate && params.endDate) {
                fetchHistoricalData([params.imei.trim()], params.startDate, params.endDate);
            }
        },
        closeMap: () => {
            modalClose();
        },
        replayTrip: () => {
            setReplayTrigger(prev => prev + 1);
        }
    }));

    useEffect(() => {
        if (data && Array.isArray(data)) {
            processHistoricalData(data);
            return;
        }

        if (formData && formData.imeis && formData.imeis.length) {
            fetchHistoricalData(formData.imeis, formData.startDate, formData.endDate);
            return;
        }

        if (imei && startDate && endDate) {
            fetchHistoricalData([imei.trim()], startDate, endDate);
            return;
        }
    }, [imei, startDate, endDate, formData, data]);

    const processHistoricalData = (dataArray) => {
        const pts = dataArray
            .filter(item => {
                const lat = parseFloat(item.latitude);
                const lng = parseFloat(item.longitude);
                return Number.isFinite(lat) && Number.isFinite(lng);
            })
            .map(item => ({
                lat: parseFloat(item.latitude),
                lng: parseFloat(item.longitude)
            }));
        setLoading(false)

        if (!pts.length) {
            setMapError('No valid location data found for this trip');
            return;
        }

        setPoints(pts);
        setStartPoint(pts[0]);
        setEndPoint(pts[pts.length - 1]);
        setIsPlaying(true);
        setReplayTrigger(prev => prev + 1);
    };

    const fetchHistoricalData = async (imeis, start, end) => {
        setMapError(null);
        setPoints([]);
        setStartPoint(null);
        setEndPoint(null);
        setIsPlaying(false);

        try {
            const requestBody = {
                imeIs: imeis,
                startDate: new Date(start).toISOString(),
                endDate: new Date(end).toISOString()
            };

            const response = await apiRunWithAxios(
                'https://api6.tappetbox.in/api/DeviceData/get-historical',
                'POST',
                access_token,
                {},
                requestBody
            );
            let dataArray = [];
            const responseData = response.data;

            if (Array.isArray(responseData)) {
                dataArray = responseData;
            } else if (Array.isArray(responseData?.data)) {
                dataArray = responseData.data;
            } else if (Array.isArray(responseData?.result)) {
                dataArray = responseData.result;
            } else if (Array.isArray(responseData?.records)) {
                dataArray = responseData.records;
            }

            const pts = dataArray
                .filter(item => {
                    const lat = parseFloat(item.latitude);
                    const lng = parseFloat(item.longitude);
                    return Number.isFinite(lat) && Number.isFinite(lng);
                })
                .map(item => ({
                    lat: parseFloat(item.latitude),
                    lng: parseFloat(item.longitude)
                }));
            setLoading(false);

            if (!pts.length) {
                setMapError('No location data available for this trip');
                return;
            }

            setPoints(pts);
            setStartPoint(pts[0]);
            setEndPoint(pts[pts.length - 1]);
            setIsPlaying(true);
            setReplayTrigger(prev => prev + 1);

        } catch (error) {
            setMapError('Failed to load trip map data');
            toast.error('Failed to load trip map data', {
                position: toast.POSITION.TOP_RIGHT
            });
        } finally {
            setLoading(false);
        }
    };

    const modalOpen = () => {
        if (modal.current) {
            modal.current.show();
        }
    };

    const modalClose = () => {
        if (modal.current) {
            modal.current.hide();
        }
    };

    useEffect(() => {
        modalElem.current = document.getElementById(modalId);
        if (modalElem.current) {
            modal.current = new Modal(modalElem.current, {
                keyboard: false,
                backdrop: false
            });

            modalElem.current.addEventListener('hidden.bs.modal', () => {
                handleClose();
            });
        }
        return () => {
            if (modal.current) {
                modal.current.dispose();
            }
        }
    }, [modalId]);

    const handleClose = () => {
        setPoints([]);
        setStartPoint(null);
        setEndPoint(null);
        setMapError(null);
        setLoading(false);
        setIsPlaying(false);
        if (onClose) {
            onClose();
        }
    };

    const handleReplay = () => {
        setReplayTrigger(prev => prev + 1);
        setIsPlaying(true);
    };

    const renderMapContent = () => {
        if (loading) {
            return (
                <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Loader />
                </div>
            );
        }

        if (mapError) {
            return (
                <div className="text-center py-5">
                    <h6 style={{ color: '#dc3545' }}>Map Error</h6>
                    <p style={{ color: '#6c757d' }}>{mapError}</p>
                </div>
            );
        }

        if (!points.length) {
            return (
                <div className="text-center py-5">No location data available</div>
            );
        }

        return (
            <div style={{
                position: 'relative',
                width: '100%',
                height: '550px',
                overflow: 'hidden',
                background: '#f5f5f5'
            }}>
                <LeafletMap 
                    points={points} 
                    startPoint={startPoint} 
                    endPoint={endPoint}
                    onReplay={(playing) => setIsPlaying(playing)}
                    replayTrigger={replayTrigger}
                />
        
            </div>
        );
    };

    return (
        <>
            <Helmet>
                <title>Trip Movable Map</title>
            </Helmet>
            <div className="modal fade" id={modalId} tabIndex="-1" ref={modalElem}>
                <div className="modal-dialog modal-lg modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header" style={{ background: '#f8f9fa' }}>
                            <h5 className="modal-title">Trip Movable Map</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="modal-body" style={{ padding: '20px', background: '#fff' }}>
                            {renderMapContent()}
                        </div>
                        <div className="modal-footer" style={{ background: '#f8f9fa' }}>
                            <button 
                                type="button" 
                                className="btn btn-primary"
                                onClick={handleReplay}
                                disabled={!points.length || loading}
                            >
                             Replay 
                            </button>
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default AnimatedMap;